import { GameState, createInitialGameState, validateMove, type Position, type PieceType } from '@/lib/chess-engine';

export interface Move {
  from: Position;
  to: Position;
  promotion?: PieceType;
}
import { sanToUci, uciToSan } from '@/lib/move-notation';
import { gameStateToFen, fenToGameState } from '@/lib/fen-parser';

export interface PgnGame {
  headers: Record<string, string>;
  moves: string[];
  result?: string;
}

export interface ParsedPgn {
  games: PgnGame[];
}

export function parsePgn(pgnText: string): ParsedPgn {
  const games: PgnGame[] = [];
  const lines = pgnText.split('\n').map(line => line.trim()).filter(line => line);
  
  let currentGame: PgnGame = { headers: {}, moves: [] };
  let moveText = '';
  let inMoves = false;
  
  for (const line of lines) {
    if (line.startsWith('[') && line.endsWith(']')) {
      // Header line - if we were collecting moves, process them first
      if (moveText.trim()) {
        currentGame.moves = parseMoveText(moveText);
        moveText = '';
      }
      
      const match = line.match(/\[(\w+)\s+"([^"]*)"\]/);
      if (match) {
        currentGame.headers[match[1]] = match[2];
      }
      inMoves = false;
    } else if (line && !line.startsWith('[')) {
      // Move line
      inMoves = true;
      
      // Check for game result first
      const resultMatch = line.match(/(1-0|0-1|1\/2-1\/2|\*)/);
      if (resultMatch) {
        // Add any remaining move text before result
        if (moveText.trim()) {
          currentGame.moves = parseMoveText(moveText);
        }
        currentGame.result = resultMatch[1];
        games.push(currentGame);
        currentGame = { headers: {}, moves: [] };
        moveText = '';
        inMoves = false;
      } else {
        // Accumulate move text
        moveText += ' ' + line;
      }
    }
  }
  
  // Process any remaining move text
  if (moveText.trim()) {
    currentGame.moves = parseMoveText(moveText);
  }
  
  // Add last game if it has content
  if (currentGame.moves.length > 0 || Object.keys(currentGame.headers).length > 0) {
    games.push(currentGame);
  }
  
  return { games };
}

function parseMoveText(text: string): string[] {
  return text
    .replace(/\d+\.\s*/g, '') // Remove move numbers with optional space
    .replace(/\{[^}]*\}/g, '') // Remove comments in braces
    .replace(/\([^)]*\)/g, '') // Remove variations in parentheses
    .replace(/\$\d+/g, '') // Remove NAGs (Numeric Annotation Glyphs)
    .replace(/\.\.\./g, '') // Remove ellipsis notation
    .replace(/[!?+#]+/g, '') // Remove annotations (but preserve in individual moves)
    .split(/\s+/)
    .map(move => move.trim())
    .filter(move => {
      if (!move || move.length === 0) return false;
      if (['1-0', '0-1', '1/2-1/2', '*'].includes(move)) return false;
      if (/^\d+\.$/.test(move)) return false; // Remove standalone move numbers
      return true;
    });
}

export function gameStatesToPgn(
  gameStates: GameState[],
  headers: Record<string, string> = {}
): string {
  if (gameStates.length === 0) return '';
  
  let pgn = '';
  
  // Add headers
  const defaultHeaders = {
    Event: '?',
    Site: '?',
    Date: new Date().toISOString().split('T')[0].replace(/-/g, '.'),
    Round: '?',
    White: '?',
    Black: '?',
    Result: '*'
  };
  
  const finalHeaders = { ...defaultHeaders, ...headers };
  
  for (const [key, value] of Object.entries(finalHeaders)) {
    pgn += `[${key} "${value}"]\n`;
  }
  
  pgn += '\n';
  
  // Add moves
  const moves: string[] = [];
  for (let i = 1; i < gameStates.length; i++) {
    const previousState = gameStates[i - 1];
    const currentState = gameStates[i];
    
    // Find the move that was made
    const lastMove = currentState.moveHistory[currentState.moveHistory.length - 1];
    if (lastMove) {
      const sanMove = uciToSan(previousState, lastMove);
      moves.push(sanMove);
    }
  }
  
  // Format moves with move numbers
  let moveText = '';
  for (let i = 0; i < moves.length; i++) {
    if (i % 2 === 0) {
      moveText += `${Math.floor(i / 2) + 1}. `;
    }
    moveText += moves[i] + ' ';
    
    // Add line breaks for readability
    if ((i + 1) % 8 === 0) {
      moveText += '\n';
    }
  }
  
  pgn += moveText.trim();
  pgn += ' ' + finalHeaders.Result;
  
  return pgn;
}

export function pgnToGameStates(pgn: string): GameState[] {
  const parsed = parsePgn(pgn);
  if (parsed.games.length === 0) return [createInitialGameState()];
  
  const game = parsed.games[0];
  const gameStates: GameState[] = [];
  
  // Start with initial position or FEN if provided
  let currentState = game.headers.FEN 
    ? fenToGameState(game.headers.FEN)
    : createInitialGameState();
  
  gameStates.push(currentState);
  
  // Apply each move
  for (const sanMove of game.moves) {
    try {
      const uci = sanToUci(currentState, sanMove);
      if (uci) {
        const move: Move = {
          from: [
            8 - parseInt(uci[1]),
            uci.charCodeAt(0) - 97
          ],
          to: [
            8 - parseInt(uci[3]),
            uci.charCodeAt(2) - 97
          ],
          promotion: uci.length > 4 ? uci[4].toUpperCase() as any : undefined
        };
        
        const moveResult = validateMove(currentState, move.from, move.to, move.promotion);
        if (moveResult.isLegal && moveResult.newState) {
          currentState = moveResult.newState;
          gameStates.push(currentState);
        } else {
          console.warn(`Invalid move: ${sanMove} (${uci})`);
          continue;
        }
      } else {
        console.warn(`Could not parse move: ${sanMove}`);
        continue;
      }
    } catch (error) {
      console.warn(`Error processing move ${sanMove}:`, error);
      continue;
    }
  }
  
  return gameStates;
}

export function isValidPgn(pgnText: string): boolean {
  try {
    const parsed = parsePgn(pgnText);
    return parsed.games.length > 0 && parsed.games[0].moves.length > 0;
  } catch {
    return false;
  }
}